import type { Task, TaskPriority } from './tasksApi';
import type { TaskStatusCategory } from './taskStatusesApi';

/**
 * Reading order inside a calendar day (Andrei, 2026-10-04): what is most
 * pressing first. Priority leads — urgent, then high, medium, low, and the
 * tasks nobody gave a priority at the end.
 *
 * Note this is NOT the API's `priorityValue` (0 None … 4 Low), where "no
 * priority" sorts first; the calendar wants it last, so the rank is its own.
 */
const PRIORITY_RANK: Record<TaskPriority, number> = {
  URGENT: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
  NONE: 4,
};

/**
 * Tie-break inside one priority: what still has to be done first, what is
 * finished last. Statuses are user-configurable, so we rank by the fixed
 * category rather than by the custom `statusOrder`, which is only meaningful
 * within one workflow.
 */
const CATEGORY_RANK: Record<TaskStatusCategory, number> = {
  UNSTARTED: 0,
  BACKLOG: 1,
  STARTED: 2,
  COMPLETED: 3,
  CANCELLED: 4,
};

/**
 * A task nobody has to act on any more. Cancelled counts as closed alongside
 * completed: neither is still "de făcut", so neither belongs above something
 * that is.
 */
const CLOSED_CATEGORIES: ReadonlySet<TaskStatusCategory> = new Set<TaskStatusCategory>([
  'COMPLETED',
  'CANCELLED',
]);

/** 0 for anything still open, 1 once it is done or cancelled. */
function closedRank(task: Task): number {
  return CLOSED_CATEGORIES.has(task.statusCategory) ? 1 : 0;
}

/**
 * Order a day's tasks: everything still open first, closed tasks after it,
 * and inside each of those two blocks by priority, then by status category
 * (Andrei, 2026-10-04).
 *
 * Open/closed leads rather than priority because a finished task is no longer
 * work — an urgent one that is done was sorting to the top of the day and
 * pushing real to-dos below it. Priority still decides the reading order among
 * the tasks that are actually left to do.
 *
 * The sort is stable, so tasks that tie on all three keep the order the API
 * returned them in (due date, then the list's own ordering) — no fourth
 * criterion is invented here.
 */
export function sortDayTasks(tasks: Task[]): Task[] {
  return [...tasks].sort(
    (a, b) =>
      closedRank(a) - closedRank(b) ||
      PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] ||
      CATEGORY_RANK[a.statusCategory] - CATEGORY_RANK[b.statusCategory],
  );
}
